import { useState, useRef, useEffect } from "react";
import {
  Menu,
  LogOut,
  User,
  ChevronDown,
} from "lucide-react";
import { cn, getInitials } from "../../lib/utils";
import { useAuthStore } from "../../stores/authStore";
import { useAppStore } from "../../stores/appStore";
import { OrgSiteSelector } from "./OrgSiteSelector";
import { useNavigate } from "react-router-dom";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";

export function TopBar() {
  const { user, logout } = useAuthStore();
  const { toggleSidebar } = useAppStore();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [confirmLogoutOpen, setConfirmLogoutOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleLogout() {
    logout();
    navigate("/login");
  }

  return (
    <>
    <header className="sticky top-0 z-30 grid h-16 grid-cols-[1fr_auto_1fr] items-center border-b border-white/10 bg-surface-900/40 backdrop-blur-2xl backdrop-saturate-150 px-4 lg:px-6 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset,0_8px_24px_rgba(2,6,23,0.35)]">
      <div className="flex items-center gap-3 justify-self-start">
        <button
          onClick={toggleSidebar}
          className="rounded-lg p-2 text-surface-400 hover:bg-surface-800 hover:text-surface-200 transition-colors lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      <div className="flex items-center justify-self-center">
        <OrgSiteSelector />
      </div>

      <div className="flex items-center gap-3 justify-self-end">
        <div ref={userMenuRef} className="relative">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className={cn(
              "flex items-center gap-2 rounded-xl p-1.5 transition-colors hover:bg-white/10",
              userMenuOpen && "bg-white/10"
            )}
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-purple-500 text-xs font-bold text-white">
              {user ? getInitials(user.full_name) : "?"}
            </div>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-surface-200 leading-tight">
                {user?.full_name || "User"}
              </p>
              <p className="text-[10px] text-surface-500 leading-tight">
                {user?.email || ""}
              </p>
            </div>
            <ChevronDown className="h-4 w-4 text-surface-400 hidden sm:block" />
          </button>

          {userMenuOpen && (
            <div className="liquid-glass-strong absolute right-0 top-full mt-2 w-56 overflow-hidden z-50">
              <div className="p-2 border-b border-white/10">
                <p className="px-3 py-1 text-sm font-medium text-surface-100">
                  {user?.full_name}
                </p>
                <p className="px-3 text-xs text-surface-400">
                  {user?.email}
                </p>
              </div>
              <div className="p-1">
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    navigate("/settings");
                  }}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-surface-200 hover:bg-white/10 transition-colors"
                >
                  <User className="h-4 w-4" />
                  Profile & Settings
                </button>
                <button
                  onClick={() => {
                    setUserMenuOpen(false);
                    setConfirmLogoutOpen(true);
                  }}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-300 hover:bg-red-500/10 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>

    <Modal
      isOpen={confirmLogoutOpen}
      onClose={() => setConfirmLogoutOpen(false)}
      title="Sign out"
      size="sm"
    >
      <div className="space-y-4">
        <p className="text-sm text-surface-300">Are you sure?</p>
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => setConfirmLogoutOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              setConfirmLogoutOpen(false);
              handleLogout();
            }}
          >
            <LogOut className="h-4 w-4" />
            Sign Out
          </Button>
        </div>
      </div>
    </Modal>
    </>
  );
}
